import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  Group,
  Loader,
  Paper,
  Radio,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { useApolloClient } from '@apollo/client';
import Breadcrumb from '@components/breadcrumb';
import TitleBar from '@components/title-bar';
import { useNarrativeStream } from '@hooks/useNarrativeStream';
import { GET_LAST_STEP } from '@queries/trip-profiles';
import type { NarrativeInputParams } from '@src/types/narrative';

function NarrativeInput() {
  const NARRATIVE_URL = import.meta.env.NARRATIVE_STREAM_URL || 'http://localhost:4000/stream-narrative';
  const { clientId, tripProfileId = '' } = useParams();
  const navigate = useNavigate();
  const apollo = useApolloClient();
  const { isStreaming, statusMessage, downloadUrl, errorMessage, send, abort } = useNarrativeStream(NARRATIVE_URL);
  const redirectedRef = useRef(false);

  // After successful generation, refresh lastStep so the breadcrumb advances
  // to step 7, then route the user back to the Narratives list so the new
  // version appears alongside prior ones. Small delay so the user sees the
  // "Narrative ready" confirmation before navigation.
  useEffect(() => {
    if (redirectedRef.current) return;
    if (!isStreaming && downloadUrl && clientId && tripProfileId) {
      redirectedRef.current = true;
      void apollo
        .query({
          query: GET_LAST_STEP,
          variables: { id: tripProfileId },
          fetchPolicy: 'network-only',
        })
        .catch(() => {
          console.warn('Failed to refetch lastStep after narrative generation.');
        });
      const timer = setTimeout(() => {
        navigate(`/clients/${clientId}/trip-profiles/${tripProfileId}/narratives`);
      }, 1500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isStreaming, downloadUrl, clientId, tripProfileId, navigate, apollo]);

  const form = useForm<NarrativeInputParams>({
    initialValues: {
      arrivalOption: 'none',
      arrivalText: '',
      addArrivalDay: false,
      departureText: '',
      addDepartureDay: false,
      nearbyOption: 'none',
      nearbyInterests: '',
      keepNearby: true,
    },
  });

  const handleGenerate = form.onSubmit(async (values) => {
    await send(tripProfileId, values);
  });

  const showArrival = form.values.arrivalOption !== 'none';
  const showNearby = form.values.nearbyOption !== 'none';

  return (
    <div>
      <Breadcrumb pb={30} pageStep={7} />
      <TitleBar mb={0} />
      <Box bg="var(--mantine-color-voyTitleBox-6)" p={10} mb={30} mt={0}>
        <Title order={2} c="white" ta="center">
          Itinerary Options
        </Title>
      </Box>

      <Container size="sm">
        <Stack gap="lg">
          <Stack gap="xs">
            <Text>
              Enhance your VoyLogic Itinerary with arrival/departure information, refined scheduling, and optional
              nearby activity suggestions.
            </Text>
            <Text size="sm" c="dimmed">
              Selected VoyLogic Attractions &amp; Activities remain the foundation of the itinerary. Any added
              activities will supplement — not replace — your selected items.
            </Text>
          </Stack>

          <Paper withBorder p="md" radius="sm">
            <Stack gap="md">
              <Title order={4}>1. Arrival &amp; Departure Information</Title>

              <Radio.Group
                label="Would you like to include arrival or departure details?"
                {...form.getInputProps('arrivalOption')}
              >
                <Stack gap="xs" mt="xs">
                  <Radio value="none" label="No" />
                  <Radio value="notes_only" label="Yes — add as reference notes only" />
                  <Radio value="logistics" label="Yes — and re-group itinerary items by proximity and schedule" />
                </Stack>
              </Radio.Group>

              {showArrival && (
                <Text size="xs" c="dimmed" fs="italic">
                  VoyLogic may adjust hotels, transfer timing, and first/last scheduled activities to better accommodate
                  travel plans.
                </Text>
              )}

              {showArrival && (
                <Stack gap="sm">
                  <Textarea
                    label="Arrival Information"
                    description="Optional — add before the first Activity Day"
                    placeholder={'ARR Iberia 233 JFK → Madrid 09:30\nArrive Málaga by train from Madrid 14:15'}
                    minRows={2}
                    autosize
                    {...form.getInputProps('arrivalText')}
                  />
                  <Checkbox
                    label="Add an arrival calendar day before the first selected activity date"
                    {...form.getInputProps('addArrivalDay', { type: 'checkbox' })}
                  />

                  <Textarea
                    label="Departure Information"
                    description="Optional — add after the last Activity Day"
                    placeholder={'DEP Málaga train to Gibraltar 13:00\nDepart Rome FCO 11:20'}
                    minRows={2}
                    autosize
                    {...form.getInputProps('departureText')}
                  />
                  <Checkbox
                    label="Add a departure calendar day after the last selected activity date"
                    {...form.getInputProps('addDepartureDay', { type: 'checkbox' })}
                  />
                </Stack>
              )}
            </Stack>
          </Paper>

          <Paper withBorder p="md" radius="sm">
            <Stack gap="md">
              <Title order={4}>2. Add Nearby Activities in Open Time Slots</Title>

              <Radio.Group
                label="Would you like VoyLogic AI to suggest additional nearby attractions or activities for unscheduled time?"
                {...form.getInputProps('nearbyOption')}
              >
                <Stack gap="xs" mt="xs">
                  <Radio value="none" label="No" />
                  <Radio
                    value="relaxed"
                    label="Yes — relaxed nearby options"
                    description="café, scenic walk, market, park, beach promenade"
                  />
                  <Radio
                    value="notable"
                    label="Yes — notable nearby sights"
                    description="cathedral, fortress, Roman ruins, museum, historic district"
                  />
                </Stack>
              </Radio.Group>

              {showNearby && (
                <Stack gap="sm">
                  <Textarea
                    label="Specify Additional Interests / Activities"
                    description="Optional — describe specific interests or experiences to include"
                    placeholder="birdwatching, bookstores, kite flying, wine tasting, food halls"
                    minRows={2}
                    autosize
                    {...form.getInputProps('nearbyInterests')}
                  />
                  <Checkbox
                    label="Keep added activities close to selected Attractions/Activities and hotel areas"
                    {...form.getInputProps('keepNearby', { type: 'checkbox' })}
                  />
                </Stack>
              )}
            </Stack>
          </Paper>

          <Stack gap={4}>
            <Text size="xs" c="dimmed">
              * Added nearby activities may not appear in the original VoyLogic Selector or VIBE RESULTS.
            </Text>
            <Text size="xs" c="dimmed">
              * Since newly suggested activities may not yet be fully mapped, VoyLogic will prioritize items located
              near selected Attractions/Activities or planned hotel areas.
            </Text>
            <Text size="xs" c="dimmed">
              * VoyLogic may re-group itinerary items by proximity and schedule when arrival/departure information or
              additional nearby activities are added.
            </Text>
          </Stack>

          {errorMessage && (
            <Alert color="red" variant="light">
              {errorMessage}
            </Alert>
          )}

          {isStreaming && (
            <Group gap="sm">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">
                {statusMessage || 'Generating narrative…'}
              </Text>
              <Button variant="subtle" color="gray" size="xs" onClick={abort}>
                Cancel
              </Button>
            </Group>
          )}

          {downloadUrl && !isStreaming && (
            <Alert color="green" variant="light" title="Narrative ready">
              <Button
                component="a"
                href={downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="filled"
                mt={8}
              >
                Download Word Document
              </Button>
            </Alert>
          )}

          {!isStreaming && (
            <Group>
              <Button onClick={() => handleGenerate()} loading={isStreaming} disabled={!tripProfileId}>
                {downloadUrl ? 'Regenerate' : 'Generate Itinerary'}
              </Button>
            </Group>
          )}
        </Stack>
      </Container>
    </div>
  );
}

export default NarrativeInput;
